import { Controller } from "@hotwired/stimulus";
import { Editor, Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Suggestion from "@tiptap/suggestion";

function createLiquidSuggestion(variables) {
  return Extension.create({
    name: "liquidSuggestion",

    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          char: "{{",
          items: ({ query }) =>
            variables.filter((v) =>
              v.label.toLowerCase().includes(query.toLowerCase()),
            ),
          command: ({ editor, range, props }) => {
            editor
              .chain()
              .focus()
              .deleteRange(range)
              .insertContent(props.value)
              .run();
          },
          render: () => {
            let dropdown;
            let selectedIndex = 0;
            let items = [];

            let commandFn;

            return {
              onStart(props) {
                commandFn = props.command;
                dropdown = document.createElement("div");
                dropdown.className = "tiptap-variable-dropdown";
                document.body.appendChild(dropdown);
                items = props.items;
                selectedIndex = 0;
                updateDropdown(dropdown, items, selectedIndex, commandFn);
                positionDropdown(dropdown, props);
              },

              onUpdate(props) {
                commandFn = props.command;
                items = props.items;
                selectedIndex = 0;
                updateDropdown(dropdown, items, selectedIndex, commandFn);
                positionDropdown(dropdown, props);
              },

              onKeyDown({ event }) {
                if (event.key === "ArrowDown") {
                  selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
                  updateSelection(dropdown, selectedIndex);
                  return true;
                }
                if (event.key === "ArrowUp") {
                  selectedIndex = Math.max(selectedIndex - 1, 0);
                  updateSelection(dropdown, selectedIndex);
                  return true;
                }
                if (event.key === "Enter" || event.key === "Tab") {
                  if (items[selectedIndex]) {
                    commandFn(items[selectedIndex]);
                    return true;
                  }
                }
                if (event.key === "Escape") {
                  return true;
                }
                return false;
              },

              onExit() {
                dropdown?.remove();
                dropdown = null;
              },
            };
          },
        }),
      ];
    },
  });
}

function updateDropdown(dropdown, items, selectedIndex, commandFn) {
  if (!items.length) {
    dropdown.hidden = true;
    return;
  }
  dropdown.hidden = false;
  dropdown.innerHTML = items
    .map(
      (item, i) =>
        `<button type="button" class="tiptap-variable-item${i === selectedIndex ? " selected" : ""}" data-index="${i}">${item.label}</button>`,
    )
    .join("");

  dropdown.querySelectorAll(".tiptap-variable-item").forEach((btn) => {
    btn.addEventListener("mousedown", (e) => {
      e.preventDefault();
      commandFn(items[parseInt(btn.dataset.index)]);
    });
  });
}

function updateSelection(dropdown, selectedIndex) {
  dropdown.querySelectorAll(".tiptap-variable-item").forEach((btn, i) => {
    btn.classList.toggle("selected", i === selectedIndex);
    if (i === selectedIndex) btn.scrollIntoView({ block: "nearest" });
  });
}

function positionDropdown(dropdown, props) {
  const rect = props.clientRect?.();
  if (!rect) return;
  dropdown.style.position = "fixed";
  dropdown.style.left = `${rect.left}px`;
  dropdown.style.top = `${rect.bottom + 4}px`;
}

export default class extends Controller {
  static targets = ["editor", "input", "toolbar"];
  static values = {
    variables: { type: Array, default: [] },
    plainText: { type: Boolean, default: false },
  };

  connect() {
    if (!this.hasInputTarget || !this.hasEditorTarget) return;
    this.initEditor();
    this.morphHandler = () => this.reinitialize();
    document.addEventListener("turbo:morph", this.morphHandler);
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.morphHandler);
    this.destroyEditor();
  }

  reinitialize() {
    this.destroyEditor();
    requestAnimationFrame(() => this.initEditor());
  }

  initEditor() {
    const inputTarget = this.inputTarget;

    const SubmitOnCmdEnter = Extension.create({
      name: "submitOnCmdEnter",
      addKeyboardShortcuts() {
        return {
          "Mod-Enter": () => {
            const form = inputTarget.form;
            if (form) form.requestSubmit();
            return true;
          },
        };
      },
    });

    const isPlainText = this.plainTextValue;

    const extensions = isPlainText
      ? [
          StarterKit.configure({
            bold: false,
            italic: false,
            strike: false,
            code: false,
            codeBlock: false,
            heading: false,
            bulletList: false,
            orderedList: false,
            listItem: false,
            blockquote: false,
            horizontalRule: false,
          }),
          Placeholder.configure({
            placeholder:
              this.editorTarget.dataset.placeholder || "Type your message...",
          }),
          SubmitOnCmdEnter,
        ]
      : [
          StarterKit.configure({
            link: {
              openOnClick: false,
              HTMLAttributes: {
                rel: "noopener noreferrer nofollow",
              },
            },
          }),
          Placeholder.configure({
            placeholder:
              this.editorTarget.dataset.placeholder || "Type your message...",
          }),
          SubmitOnCmdEnter,
        ];

    if (this.variablesValue.length) {
      extensions.push(createLiquidSuggestion(this.variablesValue));
    }

    this.editor = new Editor({
      element: this.editorTarget,
      extensions,
      content: isPlainText
        ? this.preparePlainTextContent(this.inputTarget.value)
        : this.prepareContent(this.inputTarget.value),
      onUpdate: ({ editor }) => {
        this.inputTarget.value = isPlainText
          ? this.getPlainText(editor)
          : editor.getHTML();
        this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }));
      },
    });

    this.updateToolbarState();
    this.editor.on("selectionUpdate", () => this.updateToolbarState());
    this.editor.on("transaction", () => this.updateToolbarState());

    // Click on empty space in the editor area to focus
    this.editorTarget.addEventListener("click", (e) => {
      if (e.target === this.editorTarget) {
        this.editor.commands.focus("end");
      }
    });

    // Make editor tabbable
    const proseMirror = this.editorTarget.querySelector("[contenteditable]");
    if (proseMirror) {
      const tabindex = this.editorTarget.dataset.tabindex || "2";
      proseMirror.setAttribute("tabindex", tabindex);
    }
  }

  destroyEditor() {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  }

  toggleBold() {
    this.editor.chain().focus().toggleBold().run();
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run();
  }

  toggleBulletList() {
    this.editor.chain().focus().toggleBulletList().run();
  }

  toggleOrderedList() {
    this.editor.chain().focus().toggleOrderedList().run();
  }

  toggleHeading() {
    this.editor.chain().focus().toggleHeading({ level: 3 }).run();
  }

  toggleLink() {
    if (this.editor.isActive("link")) {
      this.editor.chain().focus().unsetLink().run();
      return;
    }

    const url = prompt("Enter URL:");
    if (
      url &&
      (/^(https?:\/\/|mailto:)/i.test(url) || /\{\{.*?\}\}/.test(url))
    ) {
      this.editor.chain().focus().setLink({ href: url }).run();
    }
  }

  getPlainText(editor) {
    const html = editor.getHTML();
    return html
      .replace(/<\/p><p>/g, "\n")
      .replace(/<br\s*\/?>/g, "\n")
      .replace(/<[^>]+>/g, "");
  }

  preparePlainTextContent(value) {
    if (!value) return "";
    return `<p>${value.replace(/\n/g, "<br>")}</p>`;
  }

  prepareContent(value) {
    if (!value) return "";
    if (/<[a-z][\s\S]*>/i.test(value)) {
      // Strip trailing <br> elements from paragraphs to prevent
      // accumulation on round-trips through the editor
      return value.replace(/<br\s*\/?>\s*(<\/p>)/gi, "$1");
    }
    return value
      .split(/\n\n+/)
      .map((block) => `<p>${block.replace(/\n/g, "<br>")}</p>`)
      .join("");
  }

  updateToolbarState() {
    if (!this.hasToolbarTarget) return;

    const buttons = this.toolbarTarget.querySelectorAll("[data-tiptap-action]");
    buttons.forEach((button) => {
      const action = button.dataset.tiptapAction;
      let isActive = false;

      switch (action) {
        case "bold":
          isActive = this.editor.isActive("bold");
          break;
        case "italic":
          isActive = this.editor.isActive("italic");
          break;
        case "bulletList":
          isActive = this.editor.isActive("bulletList");
          break;
        case "orderedList":
          isActive = this.editor.isActive("orderedList");
          break;
        case "heading":
          isActive = this.editor.isActive("heading", { level: 3 });
          break;
        case "link":
          isActive = this.editor.isActive("link");
          break;
      }

      button.classList.toggle("active", isActive);
    });
  }
}
